import { useState, useEffect } from "react";
import { AdminLayout } from "./AdminDashboard";
import API from "../../lib/api";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Briefcase, Save, Eye, EyeOff } from "lucide-react";

export default function AdminOfferwalls() {
  const [settings, setSettings] = useState({});
  const [form, setForm] = useState({ bitcotasks_api_key: "", bitcotasks_secret: "", bitcotasks_enabled: false, bitcotasks_ips: "", offerwallmedia_pub_id: "", offerwallmedia_secret: "", offerwallmedia_enabled: false, offerwallmedia_ips: "", min_offerwall_reward: 0.01 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    API.get("/admin/offerwalls").then(r => { setSettings(r.data); setForm({...r.data, bitcotasks_ips: r.data.bitcotasks_ips ?? "", offerwallmedia_ips: r.data.offerwallmedia_ips ?? "", min_offerwall_reward: r.data.min_offerwall_reward || 0.01}); }).catch(() => {});
  }, []);

  const save = async () => {
    setLoading(true);
    try {
      await API.put("/admin/offerwalls", form);
      toast.success("Offerwall settings saved");
    } catch { toast.error("Failed"); }
    finally { setLoading(false); }
  };

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  return (
    <AdminLayout title="Offerwalls (Jobs)">
      <div className="space-y-6 max-w-4xl" data-testid="admin-offerwalls">
        <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-6 space-y-5">
          <div className="flex items-center gap-3">
            <Briefcase className="w-5 h-5 text-[#FFD600]" />
            <h3 className="text-lg font-bold text-[#F8FAFC]">Offerwall Configuration</h3>
          </div>

          {/* Postback URLs info */}
          <div className="bg-[#0B0E14] rounded-lg p-4 border border-[#2A3241]/30">
            <p className="text-xs font-bold text-[#94A3B8] mb-2 uppercase tracking-wider">Postback URLs (set these in provider dashboards)</p>
            <div className="space-y-2">
              <div>
                <p className="text-xs text-[#00F0FF] font-bold mb-1">BicoTasks:</p>
                <p className="text-[10px] font-mono-game text-[#94A3B8] break-all bg-[#131722] p-2 rounded">{BACKEND_URL}/api/postback/bitcotasks?subId=[subId]&reward=[reward]&transId=[transId]&signature=[signature]</p>
              </div>
              <div>
                <p className="text-xs text-[#B026FF] font-bold mb-1">OfferwallMedia:</p>
                <p className="text-[10px] font-mono-game text-[#94A3B8] break-all bg-[#131722] p-2 rounded">{BACKEND_URL}/api/postback/offerwallmedia?user_id=[user_id]&value=[value]&token=[token]&signature=[signature]</p>
              </div>
              <p className="text-[9px] text-[#64748B]">Replace brackets with provider macros. Both endpoints also accept generic params (user_id, amount, offer_id, sig) as fallback.</p>
            </div>
          </div>

          {/* BicoTasks */}
          <div className="border border-[#2A3241]/30 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-bold text-[#F8FAFC]">BicoTasks</h4>
              <button onClick={() => setForm({ ...form, bitcotasks_enabled: !form.bitcotasks_enabled })} data-testid="toggle-bitcotasks"
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase rounded-lg border ${form.bitcotasks_enabled ? "border-[#10B981]/30 bg-[#10B981]/10 text-[#10B981]" : "border-[#2A3241] text-[#64748B]"}`}>
                {form.bitcotasks_enabled ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                {form.bitcotasks_enabled ? "Enabled" : "Disabled"}
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[#94A3B8] text-xs">API Key (Publisher ID)</Label>
                <Input value={form.bitcotasks_api_key || ""} onChange={(e) => setForm({ ...form, bitcotasks_api_key: e.target.value })} placeholder="32-char API key from bitcotasks.com"
                  className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" data-testid="bitcotasks-key-input" />
              </div>
              <div>
                <Label className="text-[#94A3B8] text-xs">Postback Secret</Label>
                <Input value={form.bitcotasks_secret || ""} onChange={(e) => setForm({ ...form, bitcotasks_secret: e.target.value })} placeholder="Your postback verification secret"
                  className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" data-testid="bitcotasks-secret-input" />
              </div>
              <div>
                <Label className="text-[#94A3B8] text-xs">Allowed Server IPs (comma-separated)</Label>
                <Input value={form.bitcotasks_ips || ""} onChange={(e) => setForm({ ...form, bitcotasks_ips: e.target.value })} placeholder="45.14.135.48"
                  className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" />
                <p className="text-[9px] text-[#64748B]">BicoTasks server: 45.14.135.48</p>
              </div>
            </div>
          </div>

          {/* OfferwallMedia */}
          <div className="border border-[#2A3241]/30 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-bold text-[#F8FAFC]">OfferwallMedia</h4>
              <button onClick={() => setForm({ ...form, offerwallmedia_enabled: !form.offerwallmedia_enabled })} data-testid="toggle-offerwallmedia"
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase rounded-lg border ${form.offerwallmedia_enabled ? "border-[#10B981]/30 bg-[#10B981]/10 text-[#10B981]" : "border-[#2A3241] text-[#64748B]"}`}>
                {form.offerwallmedia_enabled ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                {form.offerwallmedia_enabled ? "Enabled" : "Disabled"}
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[#94A3B8] text-xs">Publisher ID</Label>
                <Input value={form.offerwallmedia_pub_id || ""} onChange={(e) => setForm({ ...form, offerwallmedia_pub_id: e.target.value })} placeholder="Publisher ID from offerwallmedia.com"
                  className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" data-testid="offerwallmedia-id-input" />
              </div>
              <div>
                <Label className="text-[#94A3B8] text-xs">Postback Secret</Label>
                <Input value={form.offerwallmedia_secret || ""} onChange={(e) => setForm({ ...form, offerwallmedia_secret: e.target.value })} placeholder="Your postback verification secret"
                  className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" data-testid="offerwallmedia-secret-input" />
              </div>
              <div>
                <Label className="text-[#94A3B8] text-xs">Allowed Server IPs</Label>
                <Input value={form.offerwallmedia_ips || ""} onChange={(e) => setForm({ ...form, offerwallmedia_ips: e.target.value })} placeholder="194.233.164.200"
                  className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" />
                <p className="text-[9px] text-[#64748B]">OfferwallMedia server: 194.233.164.200</p>
              </div>
            </div>
          </div>

          <div className="bg-[#0B0E14] rounded-lg p-4 border border-[#2A3241]/30 space-y-3">
            <h4 className="text-sm font-bold text-[#F8FAFC]">Security Settings</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[#94A3B8] text-xs">Min Reward Amount (coins)</Label>
                <Input type="number" step="0.01" value={form.min_offerwall_reward || 0.01} onChange={(e) => setForm({ ...form, min_offerwall_reward: parseFloat(e.target.value) })}
                  className="bg-[#131722] border-[#2A3241] text-[#F8FAFC] h-8 text-sm" />
                <p className="text-[9px] text-[#64748B]">Reject postbacks below this amount</p>
              </div>
            </div>
            <p className="text-[9px] text-[#64748B]">IP whitelist blocks postbacks from unknown servers. Status check rejects clicks (only credits completions). Chargebacks auto-reverse.</p>
          </div>

          <button onClick={save} disabled={loading} data-testid="save-offerwalls-btn"
            className="flex items-center gap-2 px-6 py-3 text-sm font-bold uppercase tracking-widest bg-[#FFD600] text-[#0B0E14] rounded-lg hover:bg-[#FFD600]/90 disabled:opacity-50">
            <Save className="w-4 h-4" /> {loading ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}
